import React, { useState, useEffect, Suspense, useContext } from "react";
import { AuthContext } from "../user/AuthContext";
import { getBuilderConfig, saveBuilderConfig } from "../firebase";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Html, useGLTF } from "@react-three/drei";
import "./BuilderPage.css";

export default function BuilderPage() {
  // — STATE —  
  const [base, setBase] = useState("Body_010");
  const [accessories, setAccessories] = useState([]);
  const [page, setPage] = useState(0);
  const perPage = 16;
  const parts = ALL_PARTS.filter((n) => n !== base);  // see ALL_PARTS below

  // — AUTH & PERSISTENCE —  
  const { user } = useContext(AuthContext);
  const [loaded, setLoaded] = useState(false);

  // 1) Load saved config once
  useEffect(() => {
    if (!user || loaded) return;
    getBuilderConfig(user.uid).then((cfg) => {
      if (cfg) {
        setBase(cfg.base);
        setAccessories(cfg.accessories);
      }
      setLoaded(true);
    });
  }, [user, loaded]);

  // 2) Save on every change
  useEffect(() => {
    if (!user || !loaded) return;
    saveBuilderConfig(user.uid, { base, accessories });
  }, [user, base, accessories, loaded]);

  // — PRELOAD MODELS —  
  useEffect(() => {
    ALL_PARTS.forEach((name) => useGLTF.preload(`/models/${name}.glb`));
  }, []);

  // — HELPERS —  
  function toggleAccessory(name) {
    setAccessories((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  }

  function GLTFModel({ url }) {
    const { scene } = useGLTF(url);
    return <primitive object={scene} dispose={null} />;
  }

  const pageItems = parts.slice(page * perPage, (page + 1) * perPage);

  // — RENDER —  
  return (
    <div className="page-container">
      <div className="viewer-panel">
        <Canvas camera={{ position: [1, 1, 4], fov: 45 }}>
          <ambientLight intensity={2} />
          <directionalLight position={[25, 75, 60]} />
          <Suspense fallback={<Html>Loading…</Html>}>
            <group position={[0, -0.6, 0]}>
              <GLTFModel url={`/models/${base}.glb`} />
              {accessories.map((name) => (
                <GLTFModel key={name} url={`/models/${name}.glb`} />
              ))}
            </group>
          </Suspense>
          <OrbitControls />
        </Canvas>
      </div>

      <div className="picker-panel">
        {pageItems.map((name) => (
          <div
            key={name}
            className={`picker-item${
              accessories.includes(name) ? " selected" : ""
            }`}
            onClick={() => toggleAccessory(name)}
          >
            {name.replace(/_/g, " ")}
          </div>
        ))}

        <div className="pagination-controls">
          <button onClick={() => setPage((p) => Math.max(p - 1, 0))} disabled={page === 0}>
            ❮
          </button>
          <span>
            {page + 1} / {Math.ceil(parts.length / perPage)}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(p + 1, Math.ceil(parts.length / perPage) - 1))}
            disabled={page >= Math.ceil(parts.length / perPage) - 1}
          >
            ❯
          </button>
        </div>
      </div>
    </div>
  );
}

// — CONSTANTS —  
const ALL_PARTS = [
  "Body_010",
  "Clown_nose_001",
  "Costume_6_001",
  "Costume_10_001",
  "Glasses_004",
  "Glasses_006",
  "Gloves_006",
  "Gloves_014",
  "Hairstyle_male_010",
  "Hairstyle_male_012",
  "Hat_010",
  "Hat_049",
  "Hat_057",
  "Headphones_002",
  "Male_emotion_angry_003",
  "Male_emotion_happy_002",
  "Male_emotion_usual_001",
  "Moustache_001",
  "Moustache_002",
  "Outwear_029",
  "Outwear_036",
  "Pacifier_001",
  "Pants_010",
  "Pants_014",
  "Shoe_Slippers_002",
  "Shoe_Slippers_005",
  "Shoe_Sneakers_009",
  "Shorts_003",
  "Socks_008",
  "T-Shirt_009",
];
